import _get from "lodash/get";
import _isMatch from "lodash/isMatch";
import _values from "lodash/values";
import _includes from "lodash/includes";
import _intersection from "lodash/intersection";
import _isNil from "lodash/isNil";
import _isNaN from "lodash/isNaN";
import _set from "lodash/set";
import _isUndefined from "lodash/isUndefined";
import _inRange from "lodash/inRange";
import _find from "lodash/find";
import _defaultTo from "lodash/defaultTo";
import _isFinite from "lodash/isFinite";
import _isString from "lodash/isString";
import _isBoolean from "lodash/isBoolean";
import "core-js/modules/es6.number.constructor.js";
import "core-js/modules/es6.function.name.js";

/** @module sanitize */

/**
 * Ensure a given input is the correct type.  Particularly useful if data comes from
 * a query string and all incoming values are Strings
 * @private
 * @param  {String} schema integer, float, boolean, string or array
 * @param  {Mixed}  value  the raw value of the input
 * @return {Mixed}         the value of the input converted to the correct type
 */
function sanitizeType(schema, value) {
  switch (schema.type) {
    case 'integer':
      if (value === '') {
        return null;
      }

      return parseInt(Number(value), 10);

    case 'float':
      if (value === '') {
        return null;
      }

      return Number(value);

    case 'boolean':
      if (_isBoolean(value)) {
        return value;
      }

      if (_isString(value)) {
        return value === 'true';
      }

      if (_isFinite(value)) {
        return value !== 0;
      }

      return null;

    case 'array':
      return Array.isArray(value) ? value : [value];

    case 'string':
    default:
      return String(value);
  }
}
/**
 * Check if an input has range boundaries and then conforms its's value to the matching range
 * @private
 * @param  {Input} input  The input
 * @param  {Mixed} value  The value of the input
 * @return {Mixed}        The value bound to the lower end of matching range
 */


function checkRange(_ref, value) {
  var range = _ref.range;

  if (range) {
    return _defaultTo(_find(range, function (_ref2) {
      var min = _ref2[0],
          max = _ref2[1];
      return _inRange(value, min, _isUndefined(max) ? Number.MAX_VALUE : max + 1);
    }), [])[0];
  }

  return value;
}

function setValueIfWhitelisted(input, result, key, rawValue) {
  if (rawValue === undefined) {
    return;
  }

  if (rawValue === null) {
    if (input.nullable) {
      _set(result, key, rawValue);
    }

    return;
  }

  var value = sanitizeType(input, rawValue);

  if (!Array.isArray(input.whitelist)) {
    var boundValue = checkRange(input, value);

    if (!_isNaN(boundValue) && !_isNil(boundValue)) {
      _set(result, key, boundValue);
    }
  } else if (input.type === 'array') {
    _set(result, key, _intersection(value, input.whitelist));
  } else if (_includes(input.whitelist, value)) {
    _set(result, key, value);
  }
}
/**
 * Ensure inputs conform to their definition and drops any input that does not conform
 * @example
 * import { sanitizeInputs } from '@nerdwallet/home-app-lib/sanitize';
 * // ...
 * const sanitizedInputs = sanitizeInputs(inputDefinitions, req.body);
 * @function
 * @param  {Array|Object} inputs     Constructed input definitions, each input should be an instance of `Input`
 * @param  {Object} data           Input data
 * @param  {Object} [filters={}]   Further filter inputs. Passed directly to `_.isMatch`
 * @return {Object}       Sanitized `values`
 */


export var sanitizeInputs = function sanitizeInputs(inputs, data) {
  var filters = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return _values(inputs).reduce(function (result, input) {
    if (input && _isMatch(input, filters)) {
      setValueIfWhitelisted(input, result, input.name, _get(data, input.name));
    }

    return result;
  }, {});
};
/**
 * Sanitize inputs meant for a query string.  Calls `sanitizeInputs` and in addition will strip sensitive inputs.
 * @function
 * @param  {Array|Object} inputs       Constructed input definitions, each input should be an instance of `Input`
 * @param  {Object} data        Input values
 * @return {Object}       Sanitized query parameters
 */

export var sanitizeQuery = function sanitizeQuery(inputs, data) {
  return sanitizeInputs(inputs, data, {
    sensitive: false
  });
};